import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle, XCircle, Loader, LogOut, AlertCircle } from 'lucide-react';
import { resignationAPI } from '../api/apiService';
import { notifySavedAndReload } from '../utils/notify';
import { useAuth, useActiveEstId } from './AuthContext';
import './ApplyLeave.css';

export default function ApproveResignation() {
  const { user } = useAuth();
  const estId = useActiveEstId?.();
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(t);
  }, [toast]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await resignationAPI.getPending(estId || null);
      setPending(res.resignations || []);
    } catch (err) {
      setToast({ type: 'error', msg: err.message || 'Could not load pending resignations.' });
    } finally {
      setLoading(false);
    }
  }, [estId]);

  useEffect(() => { load(); }, [load]);

  const decide = async (req, status) => {
    const verb = status === 'approved' ? 'Approve' : 'Reject';
    const empLabel = `${req.first_name || ''} ${req.last_name || ''}`.trim() || req.employee_id;
    const comments = window.prompt(
      `${verb} resignation for ${empLabel}?\nLast working day: ${req.last_working_day}\n\nOptional comment:`,
      '',
    );
    if (comments === null) return;
    if (status === 'approved' && !window.confirm(
      `Confirm approval: ${empLabel} will be marked as exited on ${req.last_working_day} and their login will be disabled.`,
    )) return;

    setBusyId(req.request_id);
    try {
      await resignationAPI.approve(req.request_id, {
        status,
        approvedBy: user?.username || 'manager',
        comments,
      });
      notifySavedAndReload(`Resignation ${status}.`);
    } catch (err) {
      setToast({ type: 'error', msg: err.message || `Failed to ${status} resignation.` });
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return <div className="al-card"><div className="al-balance-loading"><Loader size={16} /> Loading…</div></div>;
  }

  return (
    <>
      {toast && (
        <div className={`al-toast ${toast.type}`}>
          {toast.type === 'success' ? <CheckCircle size={18} /> : <XCircle size={18} />}
          <span>{toast.msg}</span>
        </div>
      )}

      <div className="al-card">
        <p className="al-card-label">
          <LogOut size={14} style={{ verticalAlign: 'middle', marginRight: '0.4rem' }} />
          Pending Resignations ({pending.length})
        </p>
        {pending.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
            <CheckCircle size={32} style={{ opacity: 0.5, marginBottom: '0.5rem' }} />
            <div>No pending resignations.</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '0.75rem' }}>
            {pending.map(r => {
              const name = `${r.first_name || ''} ${r.last_name || ''}`.trim() || r.employee_id;
              return (
                <div key={r.request_id} style={{
                  padding: '1rem', border: '1px solid #e5e7eb', borderRadius: '8px',
                  background: 'var(--theme-surface, #fff)',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: '1rem' }}>{name}</div>
                      <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '0.25rem' }}>
                        ID: {r.employee_id} · Submitted {r.applied_date?.slice(0, 10)}
                      </div>
                      <div style={{ marginTop: '0.5rem', display: 'flex', gap: '1.25rem', fontSize: '0.85rem' }}>
                        <span><strong>Resigning:</strong> {r.resignation_date}</span>
                        <span><strong>Last day:</strong> {r.last_working_day}</span>
                        <span><strong>Notice:</strong> {r.notice_period_days} days</span>
                      </div>
                      {r.reason && (
                        <div style={{ marginTop: '0.5rem', padding: '0.5rem 0.75rem', background: '#f9fafb', borderRadius: '6px', fontSize: '0.85rem' }}>
                          <strong>Reason:</strong> {r.reason}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <button onClick={() => decide(r, 'approved')} disabled={busyId === r.request_id}
                        className="mp-btn-save" style={{ background: '#10b981', minWidth: '110px' }}>
                        {busyId === r.request_id ? <Loader size={14} className="mp-spinner-sm" /> : <CheckCircle size={14} />}
                        Approve
                      </button>
                      <button onClick={() => decide(r, 'rejected')} disabled={busyId === r.request_id}
                        className="mp-btn-cancel" style={{ background: '#fee2e2', color: '#991b1b', minWidth: '110px' }}>
                        <XCircle size={14} /> Reject
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#fef3c7', borderRadius: '6px', fontSize: '0.8rem', color: '#92400e', display: 'flex', gap: '0.5rem' }}>
          <AlertCircle size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
          <span>Approving will mark the employee as exited (effective their last working day) and disable their login.</span>
        </div>
      </div>
    </>
  );
}
